import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Upload, FileText, Download } from 'lucide-react';
import { api, fileUrl } from '../lib/api';

export function DrawingDetailModal({ drawingId, onClose, onUpdate }) {
  const [d, setD] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const all = await api.get('/drawings');
    const item = all.data.find(x => x.id === drawingId);
    setD(item);
  };
  useEffect(() => { if (drawingId) load(); }, [drawingId]);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append('file', f);
      if (d.property_id) form.append('property_id', d.property_id);
      form.append('category', 'drawing'); form.append('title', d.title);
      const r = await api.post('/files/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.patch(`/drawings/${drawingId}`, { file_id: r.data.file_id });
      await load();
      onUpdate?.();
    } finally { setUploading(false); }
  };

  const setStatus = async (status) => {
    await api.patch(`/drawings/${drawingId}`, { status });
    await load(); onUpdate?.();
  };

  if (!d) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-testid="drawing-modal-view">
      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} onClick={(e)=>e.stopPropagation()}
                  className="glass-strong w-full max-w-xl p-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline">Drawing</div>
            <div className="font-serif text-2xl">{d.title}</div>
            <div className="text-sm text-slate-500 mt-1">{d.discipline} · {d.revision}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-full"><X size={18}/></button>
        </div>
        <div className="text-sm space-y-1 mb-5">
          <div><span className="text-slate-500">Consultant:</span> {d.consultant || '—'}</div>
          <div><span className="text-slate-500">Licence:</span> <span className="font-mono">{d.consultant_license || '—'}</span></div>
        </div>
        <div className="mb-5">
          <div className="overline mb-2">Status</div>
          <div className="flex gap-2 flex-wrap">
            {['for_approval','approved','superseded','as_built'].map(s => (
              <button key={s} onClick={() => setStatus(s)}
                      className={`status-pill text-xs cursor-pointer ${d.status === s ? 'status-success' : 'status-neutral'}`}>{s.replace('_',' ')}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="overline mb-2">File</div>
          {d.file_id ? (
            <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 rounded-full text-sm">
              <FileText size={14}/> View drawing <Download size={12}/>
            </a>
          ) : (
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 rounded-full text-sm cursor-pointer" data-testid="upload-drawing-file">
              <Upload size={14}/> {uploading ? 'Uploading…' : 'Attach drawing file'}
              <input type="file" className="hidden" onChange={uploadFile}/>
            </label>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export function PermitDetailModal({ permitId, onClose, onUpdate }) {
  const [p, setP] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const all = await api.get('/permits');
    setP(all.data.find(x => x.id === permitId));
  };
  useEffect(() => { if (permitId) load(); }, [permitId]);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append('file', f);
      if (p.property_id) form.append('property_id', p.property_id);
      form.append('category', 'permit_stamped'); form.append('title', `${p.authority} - ${p.permit_type}`);
      const r = await api.post('/files/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.patch(`/permits/${permitId}`, { stamped_file_id: r.data.file_id });
      await load(); onUpdate?.();
    } finally { setUploading(false); }
  };

  const setStatus = async (status) => {
    await api.patch(`/permits/${permitId}`, { status });
    await load(); onUpdate?.();
  };

  if (!p) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-testid="permit-modal-view">
      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} onClick={(e)=>e.stopPropagation()}
                  className="glass-strong w-full max-w-xl p-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline">Authority submission</div>
            <div className="font-serif text-2xl">{p.authority}</div>
            <div className="text-sm text-slate-500 mt-1">{p.permit_type}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-full"><X size={18}/></button>
        </div>
        <div className="text-sm space-y-1 mb-5">
          <div><span className="text-slate-500">Reference:</span> <span className="font-mono">{p.reference_number || '—'}</span></div>
          <div><span className="text-slate-500">Fee paid:</span> AED {p.fee_paid_aed?.toLocaleString() || 0}</div>
          <div><span className="text-slate-500">Submitted:</span> {p.submission_date ? new Date(p.submission_date).toLocaleDateString() : '—'}</div>
          <div><span className="text-slate-500">Approved:</span> {p.approval_date ? new Date(p.approval_date).toLocaleDateString() : '—'}</div>
        </div>
        <div className="mb-5">
          <div className="overline mb-2">Status</div>
          <div className="flex gap-2 flex-wrap">
            {['submitted','under_review','approved','rejected','resubmit'].map(s => (
              <button key={s} onClick={() => setStatus(s)}
                      className={`status-pill text-xs cursor-pointer ${p.status === s ? (s === 'approved' ? 'status-success' : s === 'rejected' ? 'status-danger' : 'status-warning') : 'status-neutral'}`}>{s.replace('_',' ')}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="overline mb-2">Stamped approval</div>
          {p.stamped_file_id ? (
            <a href={fileUrl(p.stamped_file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 rounded-full text-sm">
              <FileText size={14}/> View stamped file <Download size={12}/>
            </a>
          ) : (
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 rounded-full text-sm cursor-pointer" data-testid="upload-permit-file">
              <Upload size={14}/> {uploading ? 'Uploading…' : 'Upload stamped file'}
              <input type="file" className="hidden" onChange={uploadFile}/>
            </label>
          )}
        </div>
      </motion.div>
    </div>
  );
}
